import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import LogLayout from "../components/LogLayout";
import SectionTabs, { ANALYTICS_TABS } from "../components/SectionTabs";
import {
  Brain, AlertTriangle, Clock, TrendingUp, Shield,
  Search, Zap, BarChart3, XCircle, CheckCircle2
} from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;
const RISK_COLORS = { LOW: "#10b981", MEDIUM: "#f59e0b", HIGH: "#ef4444", CRITICAL: "#dc2626", UNKNOWN: "#64748b" };
const RISK_LABELS = { LOW: "Faible", MEDIUM: "Moyen", HIGH: "Eleve", CRITICAL: "Critique", UNKNOWN: "Inconnu" };

export default function AIPredictivePage() {
  const [predictions, setPredictions] = useState([]);
  const [anomalies, setAnomalies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [tab, setTab] = useState("predictions");
  const location = useLocation();
  const isLogContext = location.pathname.startsWith("/log/");

  const loadPredictions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai/predict-all-insurers`, { headers: getHeaders() });
      setPredictions(res.data.predictions || []);
    } catch { }
  }, []);

  const loadAnomalies = async () => {
    setScanning(true);
    try {
      const res = await axios.get(`${API}/ai/scan-anomalies`, { headers: getHeaders() });
      setAnomalies(res.data);
      toast.success(`${res.data.anomalies_found} anomalie(s) detectee(s) sur ${res.data.total_scanned} factures`);
    } catch (e) { toast.error("Erreur lors du scan"); }
    finally { setScanning(false); }
  };

  useEffect(() => {
    loadPredictions().then(() => setLoading(false));
  }, [loadPredictions]);

  const chartData = predictions.slice(0, 10).map(p => ({
    name: p.insurer.length > 12 ? p.insurer.substring(0, 12) + "..." : p.insurer,
    délai: p.predicted_days,
    risk: p.risk_level,
    rejet: p.rejection_rate,
  }));

  if (loading) {
    return (
      <LogLayout title={isLogContext ? "Analytics & Prédictions" : "IA Prédictive"} subtitle={isLogContext ? "Prédictions IA : retards et anomalies" : "Predictions de retards et detection d'anomalies"}>
        {isLogContext && <SectionTabs tabs={ANALYTICS_TABS} />}
        <div className="flex justify-center py-16">
          <div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </LogLayout>
    );
  }

  return (
    <LogLayout title={isLogContext ? "Analytics & Prédictions" : "IA Prédictive"} subtitle={isLogContext ? "Prédictions IA : retards de paiement et anomalies" : "Predictions de retards de paiement et detection d'anomalies"}>
      {isLogContext && <SectionTabs tabs={ANALYTICS_TABS} />}
      <div data-testid="ai-prédictive-page" className="space-y-6">
        {/* Tabs */}
        <div className="flex gap-2">
          <Button data-testid="tab-predictions" onClick={() => setTab("predictions")} variant={tab === "predictions" ? "default" : "outline"} className={tab === "predictions" ? "bg-sky-600 text-white" : "border-slate-200 text-slate-600"}>
            <Clock className="w-4 h-4 mr-1.5" /> Prédictions de retard
          </Button>
          <Button data-testid="tab-anomalies" onClick={() => { setTab("anomalies"); if (!anomalies) loadAnomalies(); }} variant={tab === "anomalies" ? "default" : "outline"} className={tab === "anomalies" ? "bg-sky-600 text-white" : "border-slate-200 text-slate-600"}>
            <AlertTriangle className="w-4 h-4 mr-1.5" /> Détection d'anomalies
          </Button>
        </div>

        {tab === "predictions" && (
          <>
            {/* Summary KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2"><Brain className="w-4 h-4 text-sky-600" /><span className="text-xs text-slate-500">Assureurs analysés</span></div>
                <p className="text-xl font-bold text-slate-900">{predictions.length}</p>
              </div>
              <div className="bg-white border border-emerald-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /><span className="text-xs text-slate-500">Risque faible</span></div>
                <p className="text-xl font-bold text-emerald-600">{predictions.filter(p => p.risk_level === "LOW").length}</p>
              </div>
              <div className="bg-white border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2"><Clock className="w-4 h-4 text-amber-500" /><span className="text-xs text-slate-500">Risque moyen</span></div>
                <p className="text-xl font-bold text-amber-600">{predictions.filter(p => p.risk_level === "MEDIUM").length}</p>
              </div>
              <div className="bg-white border border-red-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2"><XCircle className="w-4 h-4 text-red-500" /><span className="text-xs text-slate-500">Risque élevé+</span></div>
                <p className="text-xl font-bold text-red-600">{predictions.filter(p => ["HIGH", "CRITICAL"].includes(p.risk_level)).length}</p>
              </div>
            </div>

            {/* Chart */}
            {chartData.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Délai prévu par assureur (jours)</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} label={{ value: "Jours", position: "insideBottom", fill: "#64748b", fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" tick={{ fill: "#475569", fontSize: 10 }} width={100} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, color: "#1e293b" }} formatter={(v, n) => [n === "délai" ? `${v} jours` : `${v}%`, n === "délai" ? "Délai prévu" : "Taux rejet"]} />
                    <Bar dataKey="délai" radius={[0, 4, 4, 0]} name="Délai prévu">
                      {chartData.map((d, i) => <Cell key={i} fill={RISK_COLORS[d.risk] || "#64748b"} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Table */}
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Détail des prédictions</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-slate-500 border-b border-slate-200">
                      <th className="text-left py-2 px-3">Assureur</th>
                      <th className="text-right py-2 px-3">Factures</th>
                      <th className="text-right py-2 px-3">En cours</th>
                      <th className="text-right py-2 px-3">Taux rejet</th>
                      <th className="text-right py-2 px-3">Délai prévu</th>
                      <th className="text-right py-2 px-3">Confiance</th>
                      <th className="text-center py-2 px-3">Risque</th>
                    </tr>
                  </thead>
                  <tbody>
                    {predictions.map((p, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2.5 px-3 text-slate-900 font-medium">{p.insurer}</td>
                        <td className="py-2.5 px-3 text-right text-slate-600">{p.total_invoices}</td>
                        <td className="py-2.5 px-3 text-right text-amber-600">{p.pending}</td>
                        <td className="py-2.5 px-3 text-right text-slate-600">{p.rejection_rate}%</td>
                        <td className="py-2.5 px-3 text-right text-slate-900 font-bold">{p.predicted_days}j</td>
                        <td className="py-2.5 px-3 text-right text-slate-600">{Math.round(p.confidence * 100)}%</td>
                        <td className="py-2.5 px-3 text-center">
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: `${RISK_COLORS[p.risk_level]}20`, color: RISK_COLORS[p.risk_level] }}>
                            {RISK_LABELS[p.risk_level]}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {tab === "anomalies" && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">
                {anomalies ? `${anomalies.anomalies_found} anomalie(s) sur ${anomalies.total_scanned} factures scannées` : "Lancez un scan pour détecter les anomalies"}
              </p>
              <Button data-testid="scan-anomalies-btn" onClick={loadAnomalies} disabled={scanning} className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold">
                <Search className={`w-4 h-4 mr-1.5 ${scanning ? "animate-spin" : ""}`} />
                {scanning ? "Scan en cours..." : "Lancer le scan"}
              </Button>
            </div>

            {anomalies && anomalies.results?.length > 0 ? (
              <div className="space-y-3">
                {anomalies.results.map((r, i) => (
                  <div key={i} data-testid={`anomaly-${i}`} className={`bg-white border rounded-xl p-4 ${
                    r.severity === "CRITICAL" ? "border-red-300" : r.severity === "HIGH" ? "border-amber-300" : "border-slate-200"
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className={`w-4 h-4 ${r.severity === "CRITICAL" ? "text-red-500" : r.severity === "HIGH" ? "text-amber-500" : "text-slate-400"}`} />
                        <span className="text-sm font-semibold text-slate-900">{r.insurer}</span>
                        <span className="text-xs text-slate-500">({new Intl.NumberFormat("fr-FR").format(r.amount)} FCFA)</span>
                      </div>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: `${RISK_COLORS[r.severity]}20`, color: RISK_COLORS[r.severity] }}>
                        {r.severity}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {r.anomalies.map((a, j) => (
                        <span key={j} className="text-[10px] px-2 py-1 rounded bg-slate-100 text-slate-600 border border-slate-200">
                          {a.replace("_", " ")}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : anomalies ? (
              <div className="text-center py-16 bg-white border border-slate-200 rounded-xl">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <p className="text-slate-900 font-medium">Aucune anomalie détectée</p>
                <p className="text-xs text-slate-500 mt-1">Toutes les factures actives sont conformes</p>
              </div>
            ) : null}
          </>
        )}
      </div>
    </LogLayout>
  );
}
